import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom'
import Button from '@mui/material/Button'

// Layout Imports
import DashboardLayout from './layouts/DashboardLayout'
import AuthLayout from './layouts/AuthLayout'

// Auth Page Imports
import Login from '@/pages/cms/auth/Login'
import ForgotPassword from '@/pages/cms/auth/ForgotPassword'
import Register from '@/pages/cms/auth/Register'
import AccountDisable from '@/pages/cms/AccountDisable'
import AccountBlocked from '@/pages/cms/AccountBlocked'
import WaitingConfirm from '@/pages/cms/WaitingConfirm'

// CMS Page Imports
import NotFound from '@/pages/cms/NotFound'
import Home from '@/pages/cms/Home'
import About from '@/pages/cms/About'
import Analytics from '@/pages/cms/Analytics'
import Employee from '@/pages/cms/users/employee/Employee'
import EmployeeDetail from '@/pages/cms/users/employee/EmployeeDetail'
import LiftUser from '@/pages/cms/users/lift/LiftUser'
import Employer from '@/pages/cms/employer/Employer'
import Attendance from '@/pages/cms/attendance/Attendance'
import AttendanceDetail from '@/pages/cms/attendance/AttendanceDetail'
import Transaction from '@/pages/cms/Transaction'
import BalancePage from '@/pages/cms/finance/balance/Balance'
import Voucher from '@/pages/cms/Voucher'
// import Settlement from '@/pages/cms/Settlement'
import Profile from '@/pages/cms/Profile'
import LiftUserDetail from './pages/cms/users/lift/LiftUserDetail'
import EmployerDetail from './pages/cms/employer/EmployerDetail'
import TransactionDetail from '@/components/transaction/TransactionDetail'
import TransactionEmployeeDetail from './components/transaction/TransactionEmployeeDetail'
import { AttendanceEmployeeDetail } from './pages/cms/attendance/AttendanceEmployeeDetail'
import { BalanceDetail } from './pages/cms/finance/balance/BalanceDetail'
import { BalanceTransactionDetail } from './components/finance/balance/BalanceTransactionDetail'
import { AttendanceActivityLog } from './pages/cms/attendance/AttendanceActivityLog'
import { EmployeeActivityLog } from './pages/cms/users/employee/EmployeeActivityLog'
import { EmployeeDetailActivityLog } from './pages/cms/users/employee/EmployeeDetailActivityLog'
import { LiftUserActivityLog } from './pages/cms/users/lift/LiftUserActivityLog'
import { LiftDetailActivityLog } from './pages/cms/users/lift/LiftDetailActivityLog'
import { SettingFinance } from './pages/cms/finance/balance/SettingFinance'
import SettingEligibilityRate from '@/pages/cms/finance/transaction/SettingEligibilityRate'
import { SettingFinanceDetail } from './pages/cms/finance/balance/SettingFinanceDetail'
import { EmployerActivityLog } from './pages/cms/employer/EmployerActivityLog'
import { EmployerDetailActivityLog } from './pages/cms/employer/EmployerDetailActivityLog'
import { TransactionActivityLog } from './pages/cms/finance/transaction/TransactionActivityLog'

// Component Imports
import ScrollToTop from '@core/components/scroll-to-top'
import GuestOnlyRoute from '@/hocs/GuestOnlyRoute'

// Config Imports
//import themeConfig from '@configs/themeConfig'

const RouteScrollRestoration = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [pathname])

  return null
}

function App() {
  return (
    <>
      <BrowserRouter>
        <RouteScrollRestoration />
        <Routes>
          {/* Root → CMS */}
          <Route path='/' element={<Navigate to='/cms' replace />} />

          {/* Redirect các path landing cũ */}
          <Route path='about' element={<Navigate to='/cms' replace />} />
          <Route path='contact' element={<Navigate to='/cms' replace />} />
          <Route path='delete-user' element={<Navigate to='/cms' replace />} />
          <Route path='privacy-policy' element={<Navigate to='/cms' replace />} />

          {/* CMS Dashboard routes with dashboard layout - Under /cms */}
          <Route path='/cms' element={<DashboardLayout />}>
            {/* Redirect /cms to home page (users/employee) */}
            {/* <Route index element={<Navigate to='users/employee' replace />} /> */}
            <Route path='home' element={<Home />} />
            <Route path='about' element={<About />} />

            {/* Analytics & Report */}
            <Route path='analytics' element={<Analytics />} />

            {/* Users */}
            <Route path='users/employee' element={<Employee />} />
            <Route path='users/employee/:id' element={<EmployeeDetail />} />
            <Route path='users/employee/activity-log' element={<EmployeeActivityLog />} />
            <Route path='users/employee/:id/activity-log' element={<EmployeeDetailActivityLog />} />
            <Route path='users/lift-user' element={<LiftUser />} />
            <Route path='users/lift-user/:id' element={<LiftUserDetail />} />
            <Route path='users/lift-user/activity-log' element={<LiftUserActivityLog />} />
            <Route path='users/lift-user/:id/activity-log' element={<LiftDetailActivityLog />} />
            {/* Other Menu Items */}
            <Route path='employer' element={<Employer />} />
            <Route path='employer/:id' element={<EmployerDetail />} />
            <Route path='employer/activity-log' element={<EmployerActivityLog />} />
            <Route path='employer/:id/activity-log' element={<EmployerDetailActivityLog />} />

            <Route path='attendance' element={<Attendance />} />
            <Route path='attendance/:id' element={<AttendanceDetail />} />
            <Route path='attendance/employee/:id' element={<AttendanceEmployeeDetail />} />
            <Route path='attendance/activity-log' element={<AttendanceActivityLog />} />

            {/* Finance */}
            <Route path='finance/transaction' element={<Transaction />} />
            <Route path='finance/transaction/:id' element={<TransactionDetail />} />
            <Route path='finance/transaction/:id/employee/:employeeId' element={<TransactionEmployeeDetail />} />
            <Route path='finance/transaction/:id/eligibility-rate' element={<SettingEligibilityRate />} />
            <Route path='finance/transaction/activity-log' element={<TransactionActivityLog />} />

            <Route path='finance/balance' element={<BalancePage />} />
            <Route path='finance/balance/:id' element={<BalanceDetail />} />
            <Route path='finance/voucher' element={<Voucher />} />
            <Route path='finance/balance/:id/transaction/:transactionId' element={<BalanceTransactionDetail />} />
            <Route path='finance/balance/setting-finance' element={<SettingFinance />} />
            <Route path='finance/balance/setting-finance/:id' element={<SettingFinanceDetail />} />

            {/* Settlement */}
            {/* <Route path='settlement' element={<Settlement />} /> */}

            {/* Profile */}
            <Route path='profile' element={<Profile />} />
          </Route>

          {/* Auth routes with GuestOnlyRoute and AuthLayout - Under /cms */}
          <Route
            path='/cms'
            element={
              <GuestOnlyRoute>
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              </GuestOnlyRoute>
            }
          >
            <Route path='login' element={<Login />} />
            <Route path='forgot-password' element={<ForgotPassword />} />
            <Route path='register' element={<Register />} />

            <Route path='account-disable' element={<AccountDisable />} />
            <Route path='account-block' element={<AccountBlocked />} />
            <Route path='account-waiting-confirm' element={<WaitingConfirm />} />
          </Route>

          {/* 404 page for CMS routes */}
          <Route path='/cms/*' element={<NotFound />} />

          <Route path='*' element={<NotFound />} />
        </Routes>

        {/* Scroll to top button */}
        <ScrollToTop className='mui-fixed'>
          <Button
            variant='contained'
            className='is-10 bs-10 rounded-full p-0 min-is-0 flex items-center justify-center'
          >
            <i className='tabler-arrow-up' />
          </Button>
        </ScrollToTop>
      </BrowserRouter>
    </>
  )
}

export default App
